/**
 * Finding things in a storyboard document, the same way on every page.
 *
 * A `StoryboardDocument` is a tree (lines → shots → slots → the take on
 * show), and every page asks the same few questions of it: which line is
 * `l03`, which line does `s07` play under, what is its first frame, which of a
 * line's voices is the one the editors use, what number does a person call
 * this line. Answered here once, so "Line 3" on the summary, the script
 * editor, the line page, the crumb, a confirm dialog and the deck is the
 * same line.
 *
 * Every number is a place, never the digits of a key. Keys are minted once,
 * past every key the storyboard ever used, deleted ones included, and they
 * survive reordering (`l07` can be the third line; with two variants left
 * of ten tried, the next one is minted `h11`), so a number read off a key
 * would be wrong the first time somebody drags a row or deletes a hook:
 *  - a **line's number** is its place among the ordinary lines, in position
 *    order from 1 - and the first of them, which opens the ad, is called
 *    **Hook 1**;
 *  - a **hook variant's number** is its place among the live hook variants,
 *    in their stored order, from 2: the first variant is Hook 2, the next
 *    Hook 3, with no gaps left by the ones deleted.
 *
 * Pure functions over the document; nothing here fetches or mutates.
 */
import type {
  StoryboardDocument,
  StoryboardLine,
  StoryboardShot,
  StoryboardSlot,
  StoryboardTake,
} from "@scandi/shared";

/** By position, ties in the order given - the same rule as the clock's. */
function byPosition<T extends { position: number }>(rows: readonly T[]): T[] {
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => a.row.position - b.row.position || a.index - b.index)
    .map(({ row }) => row);
}

export const isHook = (line: Pick<StoryboardLine, "kind">): boolean => line.kind === "hook";

/** The script, in order, without the hook variants. */
export function ordinaryLines(doc: Pick<StoryboardDocument, "lines">): StoryboardLine[] {
  return byPosition(doc.lines.filter((line) => !isHook(line)));
}

/** The hook variants, in order. */
export function hookLines(doc: Pick<StoryboardDocument, "lines">): StoryboardLine[] {
  return byPosition(doc.lines.filter(isHook));
}

export function findLine(doc: Pick<StoryboardDocument, "lines">, key: string | undefined): StoryboardLine | null {
  if (!key) return null;
  return doc.lines.find((line) => line.key === key) ?? null;
}

/** A shot by key, wherever it is: under a line or loose. */
export function findShot(
  doc: Pick<StoryboardDocument, "lines" | "looseShots">,
  key: string | undefined,
): StoryboardShot | null {
  if (!key) return null;
  for (const line of doc.lines) {
    const shot = line.shots.find((candidate) => candidate.key === key);
    if (shot) return shot;
  }
  return doc.looseShots.find((shot) => shot.key === key) ?? null;
}

/**
 * What a person calls this line, by its place (the rules above):
 * `{ kind: "hook", n: 1 }` for the first ordinary line, `{ kind: "line", n: 3 }`
 * for the third, `{ kind: "hook", n: 2 }` for the first hook variant whatever
 * its key. Null for a key the document does not have. A page that shows an
 * order the server has not answered yet (a move on its way) passes the lines
 * in that order, positions renumbered, and gets the names it will have.
 */
export function lineNumber(
  doc: Pick<StoryboardDocument, "lines">,
  key: string | undefined,
): { kind: "line" | "hook"; n: number } | null {
  const line = findLine(doc, key);
  if (!line) return null;
  if (isHook(line)) return { kind: "hook", n: hookLines(doc).indexOf(line) + 2 };
  const n = ordinaryLines(doc).indexOf(line) + 1;
  return n === 1 ? { kind: "hook", n } : { kind: "line", n };
}

/** The slots of one kind (`frame`, `clip`, `voiceover`, `reference_still`), in order. */
export function slotsOfKind(owner: { slots: readonly StoryboardSlot[] }, kind: string): StoryboardSlot[] {
  return byPosition(owner.slots.filter((slot) => slot.kind === kind));
}

/** A shot's frame options, v1 first. */
export const frameSlots = (shot: Pick<StoryboardShot, "slots">) => slotsOfKind(shot, "frame");

/** A shot's clip, when it has one. */
export const clipSlot = (shot: Pick<StoryboardShot, "slots">): StoryboardSlot | null =>
  slotsOfKind(shot, "clip")[0] ?? null;

/** The still from the reference this shot adapts, when there is one. */
export const referenceStillSlot = (shot: Pick<StoryboardShot, "slots">): StoryboardSlot | null =>
  slotsOfKind(shot, "reference_still")[0] ?? null;

/** The first frame option that has a picture: what a thumbnail of the shot shows. */
export function firstFrameTake(shot: Pick<StoryboardShot, "slots">): StoryboardTake | null {
  for (const slot of frameSlots(shot)) if (slot.current) return slot.current;
  return null;
}

/** A line's voiceover slots, one per voice alternative. */
export const voiceSlots = (line: Pick<StoryboardLine, "slots">) => slotsOfKind(line, "voiceover");

/**
 * The voice the editors should use: the slot `meta.voiceSlotKey` names, else
 * the first one with a take, else the first one.
 */
export function primaryVoiceSlot(line: Pick<StoryboardLine, "slots" | "meta">): StoryboardSlot | null {
  const slots = voiceSlots(line);
  const named = typeof line.meta.voiceSlotKey === "string" ? line.meta.voiceSlotKey : null;
  return (
    (named ? slots.find((slot) => slot.key === named) : undefined) ??
    slots.find((slot) => slot.current) ??
    slots[0] ??
    null
  );
}

/** The storyboard-level slot of a kind (`reference_video`, `final_cut`), when there is one. */
export function mediaSlot(doc: Pick<StoryboardDocument, "media">, kind: string, key?: string): StoryboardSlot | null {
  return doc.media.find((slot) => slot.kind === kind && (key === undefined || slot.key === key)) ?? null;
}

/** Whether a take's master is a picture the browser can draw, as opposed to a clip or a sound. */
export const isImageTake = (take: Pick<StoryboardTake, "contentType"> | null | undefined): boolean =>
  Boolean(take?.contentType?.startsWith("image/"));

export const isVideoTake = (take: Pick<StoryboardTake, "contentType"> | null | undefined): boolean =>
  Boolean(take?.contentType?.startsWith("video/"));

/** Whether a take has something to draw as a still: its thumbnail, a clip's poster, or the picture itself. */
export const hasPicture = (take: Pick<StoryboardTake, "contentType" | "files"> | null | undefined): boolean =>
  Boolean(take && (take.files.thumb || take.files.poster || isImageTake(take)));
